import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Button, DRAWER_Z_INDEX, Input, Label, Select, Switch, useToasts } from '@treams/treams-ui';

import { ScoreCategory } from '../../../../packages/@treams/treams-analytics/src/score/models/ScoreTypes';
import {
	type ScoreConfigOverrides,
	currentScoreConfigOverrides,
} from '../../../../packages/@treams/treams-analytics/src/score/services/ScoreConfig';
import {
	ScoreConfigPersistError,
	ScoreRecalculationError,
	clearScoreConfigOverrides,
	loadScoreConfigOverrides,
	saveScoreConfigOverrides,
} from './scoreConfigOverrides';

type FieldDef = {
	id: string;
	labelKey: string;
	labelDefault: string;
	labelOptions?: Record<string, string | number>;
	/** Draft UI shows 0–100 with "%" suffix; stored value stays 0–1. */
	asPercent?: boolean;
	suffixKey?: string;
	suffixDefault?: string;
	read: (overrides: ScoreConfigOverrides) => number;
	write: (overrides: ScoreConfigOverrides, value: number) => void;
};

type Section = {
	id: string;
	titleKey: string;
	titleDefault: string;
	tabLabelKey?: string;
	tabLabelDefault?: string;
	fields: FieldDef[];
};

const weightField = (
	labelKey: string,
	labelDefault: string,
	category: ScoreCategory,
): FieldDef => ({
	id: `weight-${category}`,
	labelKey,
	labelDefault,
	asPercent: true,
	read: (overrides) => overrides.weights[category],
	write: (overrides, value) => {
		overrides.weights[category] = value;
	},
});

const bandField = (index: number, key: 'maxDays' | 'score'): FieldDef => ({
	id: `band-${index}-${key}`,
	labelKey:
		key === 'maxDays'
			? 'settings.analytics.scoreParams.field.band1MaxDays'
			: 'settings.analytics.scoreParams.field.band1Score',
	labelDefault: key === 'maxDays' ? 'Krijgt een: tot' : 'Krijgt een',
	suffixKey:
		key === 'maxDays'
			? 'settings.analytics.scoreParams.unit.days'
			: 'settings.analytics.scoreParams.unit.score',
	suffixDefault: key === 'maxDays' ? 'dagen' : 'score',
	read: (overrides) => overrides.oneOnOne.gapBands[index]?.[key] ?? 0,
	write: (overrides, value) => {
		const band = overrides.oneOnOne.gapBands[index];

		if (band) band[key] = value;
	},
});

const WEIGHTS_SECTION: Section = {
	id: 'weights',
	titleKey: 'settings.analytics.scoreParams.section.weights',
	titleDefault: 'Weging',
	fields: [
		weightField('analytics.treamsScore.factor.engagement', 'Engagement', ScoreCategory.Engagement),
		weightField('analytics.treamsScore.factor.oneOnOne', '1-op-1', ScoreCategory.OneOnOne),
		weightField('analytics.treamsScore.factor.targets', 'Targets', ScoreCategory.Targets),
		weightField('analytics.treamsScore.factor.feedback', 'Feedback', ScoreCategory.Feedback),
		weightField(
			'analytics.treamsScore.factor.conversations',
			'Conversations',
			ScoreCategory.Conversations,
		),
	],
};

const TAB_SECTIONS: Section[] = [
	{
		id: 'windows',
		titleKey: 'settings.analytics.scoreParams.section.windows',
		titleDefault: 'Tijdvensters',
		tabLabelKey: 'settings.analytics.scoreParams.section.windows',
		tabLabelDefault: 'Tijdvensters',
		fields: [
			{
				id: 'rolling-window',
				labelKey: 'settings.analytics.scoreParams.field.rollingWindow',
				labelDefault: 'Rolling window',
				suffixKey: 'settings.analytics.scoreParams.unit.months',
				suffixDefault: 'maanden',
				read: (overrides) => overrides.windows.rollingWindowMonths,
				write: (overrides, value) => {
					overrides.windows.rollingWindowMonths = value;
				},
			},
			{
				id: 'conversations-lookback',
				labelKey: 'settings.analytics.scoreParams.field.conversationsLookback',
				labelDefault: 'Conversations lookback',
				suffixKey: 'settings.analytics.scoreParams.unit.days',
				suffixDefault: 'dagen',
				read: (overrides) => overrides.windows.conversationsLookbackDays,
				write: (overrides, value) => {
					overrides.windows.conversationsLookbackDays = value;
				},
			},
		],
	},
	{
		id: 'engagement',
		titleKey: 'settings.analytics.scoreParams.section.engagement',
		titleDefault: 'Engagement',
		tabLabelKey: 'analytics.treamsScore.factor.engagement',
		tabLabelDefault: 'Engagement',
		fields: [
			{
				id: 'participation-threshold',
				labelKey: 'settings.analytics.scoreParams.field.participationThreshold',
				labelDefault: 'Participatiedrempel (0-1, strikt boven)',
				read: (overrides) => overrides.engagement.participationThreshold,
				write: (overrides, value) => {
					overrides.engagement.participationThreshold = value;
				},
			},
		],
	},
	{
		id: 'oneOnOne',
		titleKey: 'settings.analytics.scoreParams.section.oneOnOne',
		titleDefault: '1-op-1 gap bands (gap < dagen, eerste match wint)',
		tabLabelKey: 'analytics.treamsScore.factor.oneOnOne',
		tabLabelDefault: '1-op-1',
		fields: [
			bandField(0, 'maxDays'),
			bandField(0, 'score'),
			bandField(1, 'maxDays'),
			bandField(1, 'score'),
			bandField(2, 'maxDays'),
			bandField(2, 'score'),
		],
	},
	{
		id: 'targets',
		titleKey: 'settings.analytics.scoreParams.section.targets',
		titleDefault: 'Targets',
		tabLabelKey: 'analytics.treamsScore.factor.targets',
		tabLabelDefault: 'Targets',
		fields: [
			{
				id: 'targets-base',
				labelKey: 'settings.analytics.scoreParams.field.baseScore',
				labelDefault: 'Basisscore',
				read: (overrides) => overrides.targets.baseScore,
				write: (overrides, value) => {
					overrides.targets.baseScore = value;
				},
			},
			{
				id: 'targets-bonus',
				labelKey: 'settings.analytics.scoreParams.field.bonusPoints',
				labelDefault: 'Bonuspunten (2×)',
				read: (overrides) => overrides.targets.bonusPoints,
				write: (overrides, value) => {
					overrides.targets.bonusPoints = value;
				},
			},
		],
	},
	{
		id: 'feedback',
		titleKey: 'settings.analytics.scoreParams.section.feedback',
		titleDefault: 'Feedback',
		tabLabelKey: 'analytics.treamsScore.factor.feedback',
		tabLabelDefault: 'Feedback',
		fields: [
			{
				id: 'feedback-base',
				labelKey: 'settings.analytics.scoreParams.field.baseScore',
				labelDefault: 'Basisscore',
				read: (overrides) => overrides.feedback.baseScore,
				write: (overrides, value) => {
					overrides.feedback.baseScore = value;
				},
			},
			{
				id: 'feedback-bonus',
				labelKey: 'settings.analytics.scoreParams.field.bonusPoints',
				labelDefault: 'Bonuspunten (2×)',
				read: (overrides) => overrides.feedback.bonusPoints,
				write: (overrides, value) => {
					overrides.feedback.bonusPoints = value;
				},
			},
			{
				id: 'feedback-on-time',
				labelKey: 'settings.analytics.scoreParams.field.onTimeRatio',
				labelDefault: 'On-time ratio (0-1)',
				read: (overrides) => overrides.feedback.onTimeRatio,
				write: (overrides, value) => {
					overrides.feedback.onTimeRatio = value;
				},
			},
			{
				id: 'feedback-min-givers',
				labelKey: 'settings.analytics.scoreParams.field.minGivers',
				labelDefault: 'Minimum givers per request',
				read: (overrides) => overrides.feedback.minGivers,
				write: (overrides, value) => {
					overrides.feedback.minGivers = value;
				},
			},
		],
	},
	{
		id: 'conversations',
		titleKey: 'settings.analytics.scoreParams.section.conversations',
		titleDefault: 'Conversations',
		tabLabelKey: 'analytics.treamsScore.factor.conversations',
		tabLabelDefault: 'Conversations',
		fields: [
			{
				id: 'conversations-base',
				labelKey: 'settings.analytics.scoreParams.field.binaryBaseScore',
				labelDefault: 'Basisscore (binair 0/x)',
				read: (overrides) => overrides.conversations.baseScore,
				write: (overrides, value) => {
					overrides.conversations.baseScore = value;
				},
			},
		],
	},
];

const ALL_FIELDS = [WEIGHTS_SECTION, ...TAB_SECTIONS].flatMap((section) => section.fields);

const toDraftValue = (field: FieldDef, value: number): string => {
	if (field.asPercent) return String(Math.round(value * 100));
	return String(value);
};

const fromDraftValue = (field: FieldDef, raw: string): number => {
	const value = Number(raw.trim().replace(',', '.'));

	if (field.asPercent) return value / 100;
	return value;
};

const toDraft = (overrides: ScoreConfigOverrides): Record<string, string> =>
	Object.fromEntries(
		ALL_FIELDS.map((field) => [field.id, toDraftValue(field, field.read(overrides))]),
	);

type FieldGridProps = {
	fields: FieldDef[];
	draft: Record<string, string>;
	fieldLabel: (field: FieldDef) => string;
	fieldSuffix: (field: FieldDef) => string | undefined;
	onChange: (id: string, value: string) => void;
	columns?: 1 | 2;
};

const FieldGrid = (props: FieldGridProps) => {
	const widthPct = props.columns === 1 ? '100%' : '50%';

	return (
		<Flex flexWrap="wrap" mx={-2}>
			{props.fields.map((field) => {
				const suffix = props.fieldSuffix(field);

				return (
					<Box
						key={field.id}
						px={2}
						mb={props.columns === 1 ? 2 : 4}
						style={{ flex: `0 0 ${widthPct}`, maxWidth: widthPct, minWidth: 0 }}
					>
						{/* Label outside Input when suffix is set — Input's suffix is centered on the
						    whole wrapper, which misplaces it if the label lives inside it. */}
						{suffix ? (
							<>
								<Label mb={2}>{props.fieldLabel(field)}</Label>
								<Input
									small
									suffix={suffix}
									value={props.draft[field.id] ?? ''}
									onChange={(value) => props.onChange(field.id, value)}
								/>
							</>
						) : (
							<Input
								small
								label={props.fieldLabel(field)}
								value={props.draft[field.id] ?? ''}
								onChange={(value) => props.onChange(field.id, value)}
							/>
						)}
					</Box>
				);
			})}
		</Flex>
	);
};

type AppliedState = {
	draft: Record<string, string>;
	enableBonuses: boolean;
};

const snapshotFromOverrides = (overrides: ScoreConfigOverrides): AppliedState => ({
	draft: toDraft(overrides),
	enableBonuses: overrides.feedback.enableBonuses,
});

export default function ScoreParamsForm() {
	const { t } = useTranslation();
	const { addToast } = useToasts();

	const initial = useMemo(
		() => snapshotFromOverrides(loadScoreConfigOverrides() ?? currentScoreConfigOverrides()),
		[],
	);

	const [draft, setDraft] = useState<Record<string, string>>(initial.draft);
	const [enableBonuses, setEnableBonuses] = useState(initial.enableBonuses);
	// Last applied config — Bijwerken stays disabled until the form differs from this.
	const [applied, setApplied] = useState<AppliedState>(initial);
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState(TAB_SECTIONS[0].id);
	const [isUpdating, setIsUpdating] = useState(false);

	const isDirty = useMemo(() => {
		if (enableBonuses !== applied.enableBonuses) return true;

		return ALL_FIELDS.some((field) => (draft[field.id] ?? '') !== (applied.draft[field.id] ?? ''));
	}, [draft, enableBonuses, applied]);

	const fieldLabel = (field: FieldDef): string =>
		t(field.labelKey, { defaultValue: field.labelDefault, ...field.labelOptions });

	const fieldSuffix = (field: FieldDef): string | undefined => {
		if (field.asPercent) return '%';
		if (!field.suffixKey) return undefined;
		return t(field.suffixKey, { defaultValue: field.suffixDefault });
	};

	const handleFieldChange = (id: string, value: string) => {
		setDraft((prev) => ({ ...prev, [id]: value }));
	};

	const buildOverrides = (): ScoreConfigOverrides | null => {
		const overrides = currentScoreConfigOverrides();

		for (const field of ALL_FIELDS) {
			const raw = draft[field.id] ?? '';
			const value = fromDraftValue(field, raw);

			if (raw.trim() === '' || !Number.isFinite(value)) {
				setError(
					t('settings.analytics.scoreParams.invalidValue', {
						defaultValue: 'Ongeldige waarde bij "{{label}}"',
						label: fieldLabel(field),
					}),
				);
				return null;
			}

			field.write(overrides, value);
		}

		overrides.feedback.enableBonuses = enableBonuses;

		setError(null);
		return overrides;
	};

	const apply = async () => {
		const overrides = buildOverrides();

		if (!overrides) return;

		setIsUpdating(true);

		try {
			await saveScoreConfigOverrides(overrides);
			setApplied({ draft: { ...draft }, enableBonuses });
			addToast(
				t('settings.analytics.scoreParams.updateSuccess', {
					defaultValue: 'Scoreparameters bijgewerkt en herberekend.',
				}),
				{ appearance: 'success', autoDismiss: true },
			);
		} catch (err) {
			if (err instanceof ScoreConfigPersistError) {
				// Nothing was stored, so the form has to stay dirty and retryable.
				setError(
					t('settings.analytics.scoreParams.saveFailed', {
						defaultValue: 'Scoreparameters konden niet worden opgeslagen: {{message}}',
						message: err.message,
					}),
				);

				return;
			}

			// Config is persisted; only the recalculation failed or never reported back.
			setApplied({ draft: { ...draft }, enableBonuses });
			addToast(
				err instanceof ScoreRecalculationError
					? t('settings.analytics.scoreParams.updateFailed', {
							defaultValue:
								'Scoreparameters opgeslagen, maar de herberekening is mislukt: {{message}}',
							message: err.message,
						})
					: t('settings.analytics.scoreParams.updateTimeout', {
							defaultValue:
								'Scoreparameters opgeslagen, maar herberekening duurde te lang. Vernieuw de pagina als scores niet updaten.',
						}),
				{ appearance: 'warning', autoDismiss: true },
			);
		} finally {
			setIsUpdating(false);
		}
	};

	const reset = async () => {
		setIsUpdating(true);

		try {
			await clearScoreConfigOverrides();
		} catch (err) {
			if (err instanceof ScoreConfigPersistError) {
				// Overrides are still stored, so keep showing the current values.
				setError(
					t('settings.analytics.scoreParams.resetFailed', {
						defaultValue: 'Parameters konden niet worden gereset: {{message}}',
						message: err.message,
					}),
				);
				setIsUpdating(false);

				return;
			}

			addToast(
				err instanceof ScoreRecalculationError
					? t('settings.analytics.scoreParams.resetRecalculationFailed', {
							defaultValue: 'Parameters gereset, maar de herberekening is mislukt: {{message}}',
							message: err.message,
						})
					: t('settings.analytics.scoreParams.resetTimeout', {
							defaultValue:
								'Parameters gereset, maar herberekening duurde te lang. Vernieuw de pagina als scores niet updaten.',
						}),
				{ appearance: 'warning', autoDismiss: true },
			);
		}

		const next = snapshotFromOverrides(currentScoreConfigOverrides());

		setDraft(next.draft);
		setEnableBonuses(next.enableBonuses);
		setApplied(next);
		setError(null);
		setIsUpdating(false);
	};
	const activeSection = TAB_SECTIONS.find((section) => section.id === activeTab) ?? TAB_SECTIONS[0];

	return (
		<Flex flexDirection="column" style={{ height: '100%' }}>
			<Box
				p={3}
				pt={4}
				pb={2}
				style={{
					flexShrink: 0,
					background: 'white',
				}}
			>
				<Box mb={2} style={{ fontWeight: 'bold' }}>
					{t(WEIGHTS_SECTION.titleKey, { defaultValue: WEIGHTS_SECTION.titleDefault })}
				</Box>
				<FieldGrid
					fields={WEIGHTS_SECTION.fields}
					draft={draft}
					fieldLabel={fieldLabel}
					fieldSuffix={fieldSuffix}
					onChange={handleFieldChange}
					columns={1}
				/>
			</Box>
			<Box px={3} pb={3} style={{ flex: 1, overflowY: 'auto' }}>
				<Box mt={3} mb={3} width={220}>
					<Select
						small
						search={false}
						zIndex={DRAWER_Z_INDEX + 2}
						selectedValue={activeTab}
						options={TAB_SECTIONS.map((section) => ({
							value: section.id,
							title: t(section.tabLabelKey ?? section.titleKey, {
								defaultValue: section.tabLabelDefault ?? section.titleDefault,
							}),
						}))}
						onChange={(value) => {
							if (value != null) setActiveTab(String(value));
						}}
					/>
				</Box>
				<FieldGrid
					fields={activeSection.fields}
					draft={draft}
					fieldLabel={fieldLabel}
					fieldSuffix={fieldSuffix}
					onChange={handleFieldChange}
					columns={activeSection.id === 'oneOnOne' ? 2 : 1}
				/>
				{activeSection.id === 'feedback' && (
					<Flex alignItems="center" mt={1}>
						<Box mr={2}>
							{t('settings.analytics.scoreParams.enableBonuses', {
								defaultValue: 'Bonussen actief',
							})}
						</Box>
						<Switch on={enableBonuses} onChange={() => setEnableBonuses((prev) => !prev)} />
					</Flex>
				)}
				{error && (
					<Box mb={2} style={{ color: 'red' }}>
						{error}
					</Box>
				)}
				<Flex mt={3} alignItems="center">
					<Box width="fit-content" mr={2}>
						<Button
							type="secondary"
							title={t('settings.analytics.scoreParams.reset', {
								defaultValue: 'Reset naar standaard',
							})}
							onClick={reset}
							disabled={isUpdating}
						/>
					</Box>
					<Box width="fit-content">
						<Button
							type="primary"
							title={t('settings.analytics.scoreParams.update', {
								defaultValue: 'Bijwerken',
							})}
							onClick={apply}
							loading={isUpdating}
							disabled={!isDirty || isUpdating}
						/>
					</Box>
				</Flex>
			</Box>
		</Flex>
	);
}
